import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-reveal: returns a ref + boolean.
 * Adds a one-shot fade/translate when the element enters the viewport.
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>(
  options: IntersectionObserverInit = { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
) => {
  const ref = useRef<T | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || shown) return;
    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setShown(true);
          obs.disconnect();
        }
      });
    }, options);
    obs.observe(node);
    return () => obs.disconnect();
  }, [shown, options]);

  return { ref, shown };
};

